import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, projectType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        projectType: formData.projectType,
        message: formData.message
      });

      if (response.status === 201) {
        setIsSubmitted(true);
        toast.success('Your message has been sent successfully!');

        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', email: '', phone: '', projectType: '', message: '' });
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    { icon: Phone, title: 'Phone', value: companyInfo.phone, link: `tel:${companyInfo.phone}` },
    { icon: Mail, title: 'Email', value: companyInfo.email, link: `mailto:${companyInfo.email}` },
    { icon: MapPin, title: 'Office', value: companyInfo.address, link: null },
    { icon: Clock, title: 'Hours', value: 'Mon - Fri: 8AM - 6PM', link: null }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-28 pb-12 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">
              Contact Us
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-4">
              Let's Build
              <span className="block text-red-500">Something Great</span>
            </h1>
            <p className="text-lg text-neutral-300 leading-relaxed">
              Ready to start your next construction project? Get in touch with
              our team to discuss your vision.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-8 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {contactMethods.map((method, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-4 border border-neutral-200 shadow-sm"
              >
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-3">
                  <method.icon className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-neutral-900 font-semibold text-sm mb-1">{method.title}</h3>
                {method.link ? (
                  <a href={method.link} className="text-red-600 hover:text-red-700 text-sm break-all">
                    {method.value}
                  </a>
                ) : (
                  <p className="text-neutral-600 text-sm">{method.value}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10">
            {/* Form */}
            <div className="bg-neutral-50 rounded-xl p-6 border border-neutral-200">
              <h2 className="text-2xl font-bold text-neutral-900 mb-1">
                Request a Quote
              </h2>
              <p className="text-neutral-500 text-sm mb-6">
                Fill out the form and we'll get back to you within 24 hours.
              </p>

              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-neutral-900 mb-1">Message Sent!</h3>
                  <p className="text-neutral-500 text-sm">We'll be in touch soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label htmlFor="name" className="text-neutral-700 text-sm">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Smith"
                        required
                        className="bg-white border-neutral-300"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="email" className="text-neutral-700 text-sm">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        required
                        className="bg-white border-neutral-300"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label htmlFor="phone" className="text-neutral-700 text-sm">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(770) 555-0123"
                        className="bg-white border-neutral-300"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="projectType" className="text-neutral-700 text-sm">Project Type *</Label>
                      <Select onValueChange={handleSelectChange} value={formData.projectType}>
                        <SelectTrigger className="bg-white border-neutral-300">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-neutral-200">
                          <SelectItem value="hospitality">Hospitality</SelectItem>
                          <SelectItem value="commercial">Commercial</SelectItem>
                          <SelectItem value="residential">Residential</SelectItem>
                          <SelectItem value="renovation">Renovation</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="message" className="text-neutral-700 text-sm">Project Details *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your project..."
                      required
                      rows={4}
                      className="bg-white border-neutral-300 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-5"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-5 h-5" />
                        Send Message
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Info Side */}
            <div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                Why Work With Us?
              </h2>
              <div className="space-y-4 mb-6">
                {[
                  { title: 'Free Initial Consultation', description: 'Discuss your project at no cost.' },
                  { title: 'Detailed Proposals', description: 'Clear scope, timeline, and pricing.' },
                  { title: 'Transparent Communication', description: 'Regular updates throughout.' },
                  { title: 'Quality Guarantee', description: 'Backed by 40+ years of experience.' }
                ].map((item, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-3 h-3 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-neutral-900 font-semibold text-sm">{item.title}</h3>
                      <p className="text-neutral-500 text-xs">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="relative rounded-xl overflow-hidden aspect-video bg-neutral-200 shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3315.8!2d-84.4!3d33.8!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDQ4JzAwLjAiTiA4NMKwMjQnMDAuMCJX!5e0!3m2!1sen!2sus!4v1699999999999"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  className="absolute inset-0"
                  title="Office Location"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
